import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getProgressMetaKey,
  getProgressTasksKey,
  getVisibleProgressColleges,
} from '../helpers/progressSource';
import { userStorage } from '../lib/userStorage';

const NotificationContext = createContext(null);

const NOTIFICATIONS_KEY = 'app_notifications';
const FIRED_REMINDERS_KEY = 'app_fired_reminders';
const NOTIFICATIONS_ENABLED_KEY = 'notifications_enabled';
const GENERAL_PROGRESS_CARDS_KEY = 'progress_general_cards';

const REMINDER_OFFSETS = {
  'day-of': { label: 'Day of', days: 0 },
  '1-day': { label: '1 day before', days: 1 },
  '3-days': { label: '3 days before', days: 3 },
  '5-days': { label: '5 days before', days: 5 },
  '1-week': { label: '1 week before', days: 7 },
};

const DEADLINE_NOTIFICATION_DAYS = [10, 5, 3, 1];

function loadFromStorage(key, fallback) {
  try {
    const raw = userStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveToStorage(key, value) {
  try {
    userStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota errors
  }
}

function getDaysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(dateStr + 'T00:00:00');
  target.setHours(0, 0, 0, 0);

  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

/**
 * Scan progress tasks and college deadlines to find notifications that are due.
 * Returns an array of new notification objects.
 */
function findDueReminders() {
  const firedSet = new Set(loadFromStorage(FIRED_REMINDERS_KEY, []));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const newNotifications = [];

  const generalCards = loadFromStorage(GENERAL_PROGRESS_CARDS_KEY, []);
  if (Array.isArray(generalCards)) {
    generalCards.forEach((card) => {
      if (!Array.isArray(card.items)) return;
      card.items.forEach((item) => {
        if (!item.date || !item.reminderOffset || item.complete) return;
        const offsetConfig = REMINDER_OFFSETS[item.reminderOffset];
        if (!offsetConfig) return;

        const taskDate = new Date(item.date + 'T00:00:00');
        const fireDate = new Date(taskDate);
        fireDate.setDate(fireDate.getDate() - offsetConfig.days);
        const reminderId = `general_${card.id}_${item.id}_${item.date}_${item.reminderOffset}`;
        if (today < fireDate || firedSet.has(reminderId)) return;

        firedSet.add(reminderId);
        newNotifications.push({
          id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          taskId: item.id,
          taskText: item.text,
          collegeName: `General Progress · ${card.title}`,
          priority: null,
          reminderOffset: item.reminderOffset,
          taskDate: item.date,
          firedAt: new Date().toISOString(),
          read: false,
        });
      });
    });
  }

  const visibleColleges = getVisibleProgressColleges();

  visibleColleges.forEach((college) => {
    const collegeName = college.name;
    const key = getProgressTasksKey(collegeName);
    try {
      const tasks = JSON.parse(userStorage.getItem(key));
      if (!Array.isArray(tasks)) return;

      tasks.forEach((task) => {
        if (!task.date || !task.reminderOffset || task.status === 'complete') return;

        const offsetConfig = REMINDER_OFFSETS[task.reminderOffset];
        if (!offsetConfig) return;

        // Calculate when the reminder should fire
        const taskDate = new Date(task.date + 'T00:00:00');
        const fireDate = new Date(taskDate);
        fireDate.setDate(fireDate.getDate() - offsetConfig.days);

        // Check if it's due and hasn't been fired
        const reminderId = `${task.id}_${task.reminderOffset}`;
        if (today >= fireDate && !firedSet.has(reminderId)) {
          firedSet.add(reminderId);

          newNotifications.push({
            id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            taskId: task.id,
            taskText: task.text,
            collegeName,
            priority: task.priority || null,
            reminderOffset: task.reminderOffset,
            taskDate: task.date,
            firedAt: new Date().toISOString(),
            read: false,
          });
        }
      });
    } catch {
      // skip corrupt data
    }
  });

  visibleColleges.forEach((college) => {
    const collegeName = college.name;
    const key = getProgressMetaKey(collegeName);
    try {
      const meta = JSON.parse(userStorage.getItem(key));
      if (!meta?.applyByDate) return;
      if (meta.applicationSubmitted) return;

      const daysUntil = getDaysUntil(meta.applyByDate);
      if (!DEADLINE_NOTIFICATION_DAYS.includes(daysUntil)) return;

      const deadlineId = `deadline_${collegeName}_${meta.applyByDate}_${daysUntil}`;
      if (firedSet.has(deadlineId)) return;

      firedSet.add(deadlineId);

      newNotifications.push({
        id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        type: 'deadline',
        collegeName,
        deadlineDate: meta.applyByDate,
        daysUntil,
        firedAt: new Date().toISOString(),
        read: false,
      });
    } catch {
      // skip corrupt data
    }
  });

  // Save updated fired set
  saveToStorage(FIRED_REMINDERS_KEY, [...firedSet]);

  return newNotifications;
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState(() =>
    loadFromStorage(NOTIFICATIONS_KEY, [])
  );
  const [toasts, setToasts] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() =>
    loadFromStorage(NOTIFICATIONS_ENABLED_KEY, true)
  );
  const hasCheckedOnMount = useRef(false);

  // Persist notifications
  useEffect(() => {
    saveToStorage(NOTIFICATIONS_KEY, notifications);
  }, [notifications]);

  useEffect(() => {
    saveToStorage(NOTIFICATIONS_ENABLED_KEY, notificationsEnabled);
    if (!notificationsEnabled) setToasts([]);
  }, [notificationsEnabled]);

  // Unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Show a toast (auto-dismiss after 5s)
  const showToast = useCallback((notification) => {
    setToasts((prev) => [...prev, notification]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== notification.id));
    }, 5000);
  }, []);

  // Dismiss a toast (manually)
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Mark a single notification as read
  const dismissNotification = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  // Remove a notification entirely
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Mark all as read
  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  // Core check function — finds due reminders, adds to notifications, shows toasts
  const checkReminders = useCallback(() => {
    if (!notificationsEnabled) return;
    const newNotifs = findDueReminders();
    if (newNotifs.length > 0) {
      setNotifications((prev) => [...newNotifs, ...prev]);
      newNotifs.forEach((n) => showToast(n));
    }
  }, [notificationsEnabled, showToast]);

  // Check once on mount and whenever reminders are turned back on.
  useEffect(() => {
    if (!notificationsEnabled) return undefined;
    if (hasCheckedOnMount.current) return undefined;
    hasCheckedOnMount.current = true;
    const timer = setTimeout(() => checkReminders(), 500);
    return () => clearTimeout(timer);
  }, [checkReminders, notificationsEnabled]);

  const updateNotificationsEnabled = useCallback((enabled) => {
    hasCheckedOnMount.current = false;
    setNotificationsEnabled(Boolean(enabled));
  }, []);

  const value = {
    notifications,
    notificationsEnabled,
    setNotificationsEnabled: updateNotificationsEnabled,
    unreadCount,
    toasts,
    checkReminders,
    showToast,
    dismissToast,
    dismissNotification,
    removeNotification,
    clearAll,
    markAllRead,
    REMINDER_OFFSETS,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}

export { REMINDER_OFFSETS };
export default NotificationContext;
